import { useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/auth-context";
import { trackEvent } from "@/hooks/useAnalytics";
import { setProductAnalyticsScreen } from "@/lib/productAnalytics";
import { toAnalyticsRoute } from "@/lib/analyticsRoute";

const PRIVATE_ROUTE_PATTERN = /^\/(admin|create-listing|dashboard|listing\/[^/]+(?:\/schedule)?|messages|my-ev|profile)(?:\/|$)/;

const normalizeAction = (value: string | null) => {
  if (!value) return null;
  const normalized = value
    .toLowerCase()
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, "id")
    .replace(/\d+/g, "id")
    .replace(/[^a-z0-9_:-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  return normalized || null;
};

const ProductAnalyticsBridge = () => {
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const root = document.getElementById("root");
    if (PRIVATE_ROUTE_PATTERN.test(location.pathname)) {
      root?.setAttribute("data-clarity-mask", "true");
    } else {
      root?.removeAttribute("data-clarity-mask");
    }

    if (location.pathname.startsWith("/admin")) return;
    const route = toAnalyticsRoute(location.pathname);
    void setProductAnalyticsScreen(route);
    void trackEvent("page_view", "pageview", {
      page_path: route,
      platform: Capacitor.getPlatform(),
    }, user?.id);
  }, [location.pathname, user?.id]);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const handleClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return;
      const target = event.target.closest<HTMLElement>("a,button,[role='button']");
      if (!target || target.closest("[data-analytics-ignore]")) return;

      const href = target instanceof HTMLAnchorElement ? target.href : null;
      const explicitAction = normalizeAction(
        target.getAttribute("data-analytics") || target.getAttribute("data-testid")
      );
      if (!href && !explicitAction) return;

      const params: Record<string, unknown> = {
        page_path: toAnalyticsRoute(location.pathname),
        target_type: target.tagName.toLowerCase(),
        ...(explicitAction ? { target_action: explicitAction } : {}),
      };

      if (href) {
        try {
          const destination = new URL(href, window.location.href);
          params.outbound = destination.origin === window.location.origin ? 0 : 1;
          params.destination_path = toAnalyticsRoute(destination.pathname);
          if (destination.origin !== window.location.origin) params.destination_host = destination.hostname;
        } catch {
          // Ignore malformed destinations rather than sending the raw value.
        }
      }

      void trackEvent("ui_click", "click", params, user?.id);
    };

    const handleSubmit = (event: SubmitEvent) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.closest("[data-analytics-ignore]")) return;
      void trackEvent("form_submit", "submit", {
        page_path: toAnalyticsRoute(location.pathname),
        form_action: normalizeAction(form.getAttribute("data-analytics") || form.id) || "page_form",
      }, user?.id);
    };

    document.addEventListener("click", handleClick, { passive: true });
    document.addEventListener("submit", handleSubmit);
    return () => {
      document.removeEventListener("click", handleClick);
      document.removeEventListener("submit", handleSubmit);
    };
  }, [location.pathname, user?.id]);

  return null;
};

export default ProductAnalyticsBridge;
